import { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Hash, ChevronDown, Users, Check, BookOpen } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatRelativeTime } from "@/utils/relativeTime";
import { Heading, Text } from "@/components/Typography";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/Avatar";
import { useAuth } from "@/hooks/useAuth";
import { useActiveWorkspace } from "@/hooks/useActiveWorkspace";
import { usePabSession } from "@/hooks/usePabSession";
import { useAdvisorPABs } from "@/hooks/useDashboardStats";
import { useExpertPrograms } from "@/hooks/useExpertPrograms";
import { api } from "@/services/api";
import type {
  ProgramMembersGrouped,
  SelectedAdvisor,
  InboxConversationPreview,
} from "@/services/api";
import { stripMessageMarkdown } from "@/utils/messageMarkdown";
import { useDrawer } from "@/context/DrawerContext";
import type { DirectMessageRecipient } from "@/components/DirectProgramMessageDrawer";

// ─── Avatar gradient pool ────────────────────────────────────────────────────

const AVATAR_GRADIENTS = [
  "var(--theme-mld-avatar-1)",
  "var(--theme-mld-avatar-2)",
  "var(--theme-mld-avatar-3)",
  "var(--theme-mld-avatar-4)",
  "var(--theme-mld-avatar-5)",
  "var(--theme-mld-avatar-6)",
];

const GROUP_CHAT_COLORS = [
  {
    bg: "var(--theme-mld-group-1-bg)",
    border: "var(--theme-mld-group-1-border)",
    icon: "var(--theme-mld-group-1-icon)",
  },
  {
    bg: "var(--theme-mld-group-2-bg)",
    border: "var(--theme-mld-group-2-border)",
    icon: "var(--theme-mld-group-2-icon)",
  },
  {
    bg: "var(--theme-mld-group-3-bg)",
    border: "var(--theme-mld-group-3-border)",
    icon: "var(--theme-mld-group-3-icon)",
  },
  {
    bg: "var(--theme-mld-group-4-bg)",
    border: "var(--theme-mld-group-4-border)",
    icon: "var(--theme-mld-group-4-icon)",
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getInitials(name: string | null | undefined): string {
  if (!name) return "?";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const SectionHeader = ({ label, count }: { label: string; count: number }) => (
  <div className="flex items-center gap-2 px-5 py-2 bg-theme-mld-section-header-bg">
    <span className="text-[10px] font-bold uppercase tracking-[1.1px] text-theme-text-muted">
      {label}
    </span>
    <span className="inline-flex items-center justify-center min-w-[19px] h-[19px] px-1 rounded-full text-[10px] font-semibold bg-theme-mld-count-badge-bg text-theme-text-muted">
      {count}
    </span>
  </div>
);

const UnreadBadge = ({ count }: { count: number }) => (
  <span
    className="inline-flex items-center justify-center size-4 rounded-full text-[9px] font-bold text-white flex-shrink-0"
    style={{ background: "var(--theme-mld-unread-badge-bg)" }}
  >
    {count > 9 ? "9+" : count}
  </span>
);

const OnlineDot = () => (
  <span className="absolute bottom-0 right-0 size-[10px] rounded-full bg-theme-mld-online-dot border-2 border-theme-mld-online-border" />
);

const RoleBadge = ({
  role,
}: {
  role: "pab_advisor" | "co_expert" | "facilitator" | "enrollee" | "peer";
}) => {
  if (role === "pab_advisor") {
    return (
      <span className="inline-flex items-center px-1.5 h-5 rounded-full text-[9px] font-semibold bg-theme-mld-pab-badge-bg border border-theme-mld-pab-badge-border text-theme-mld-pab-badge-text whitespace-nowrap">
        Advisory Board Expert
      </span>
    );
  }
  // A peer group's staff are Facilitators and its members are Peers. Same pill
  // treatment, different word -- calling a facilitator a "Co-Expert" here would
  // contradict every other peer surface.
  if (role === "co_expert" || role === "facilitator") {
    return (
      <span className="inline-flex items-center px-1.5 h-5 rounded-full text-[9px] font-semibold bg-theme-mld-co-expert-badge-bg border border-theme-mld-co-expert-badge-border text-theme-mld-co-expert-badge-text whitespace-nowrap">
        {role === "facilitator" ? "Facilitator" : "Co-Expert"}
      </span>
    );
  }
  if (role === "peer") {
    return (
      <span className="inline-flex items-center px-1.5 h-5 rounded-full text-[9px] font-semibold bg-theme-mld-pab-badge-bg border border-theme-mld-pab-badge-border text-theme-mld-pab-badge-text whitespace-nowrap">
        Peer
      </span>
    );
  }
  return null;
};

// Group chat row (PAB group or Program cohort)
const GroupChatRow = ({
  name,
  lastMessage,
  lastMessageTime,
  unreadCount,
  memberCount,
  colorIndex,
  onClick,
}: {
  name: string;
  lastMessage?: string | null;
  lastMessageTime?: string | null;
  unreadCount?: number;
  memberCount?: number;
  colorIndex: number;
  onClick: () => void;
}) => {
  const color = GROUP_CHAT_COLORS[colorIndex % GROUP_CHAT_COLORS.length];
  const relTime = formatRelativeTime(lastMessageTime);

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex gap-3.5 items-center px-5 py-3.5 border-b border-theme-mld-border hover:bg-theme-mld-row-hover transition-colors text-left"
    >
      {/* Group icon avatar */}
      <div
        className="size-10 rounded-full flex items-center justify-center flex-shrink-0 border-[1.5px]"
        style={{ background: color.bg, borderColor: color.border }}
      >
        <Hash size={15} style={{ color: color.icon }} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-0.5">
          <span className="text-[14px] font-semibold text-theme-mld-heading truncate leading-5">
            {name}
          </span>
          <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
            {relTime && <span className="text-[11px] text-theme-text-muted">{relTime}</span>}
            {!!unreadCount && <UnreadBadge count={unreadCount} />}
          </div>
        </div>
        {lastMessage ? (
          <p className="text-[11px] text-theme-mld-preview-text truncate leading-[16.5px]">
            {lastMessage}
          </p>
        ) : (
          <p className="text-[11px] text-theme-text-muted truncate leading-[16.5px] italic">
            {memberCount != null
              ? `No messages yet · ${memberCount} member${memberCount !== 1 ? "s" : ""}`
              : "No messages yet"}
          </p>
        )}
      </div>
    </button>
  );
};

// DM row (PAB advisor, co-expert, enrollee)
const DmRow = ({
  name,
  roleBadge,
  avatarGradient,
  photoUrl,
  isOnline,
  lastMessage,
  lastMessageTime,
  unreadCount,
  currentRole,
  company,
  onClick,
}: {
  name: string;
  roleBadge?: "pab_advisor" | "co_expert" | "facilitator" | "enrollee" | "peer";
  avatarGradient?: string;
  photoUrl?: string | null;
  isOnline?: boolean;
  lastMessage?: string | null;
  lastMessageTime?: string | null;
  unreadCount?: number;
  currentRole?: string | null;
  company?: string | null;
  onClick: () => void;
}) => {
  const relTime = formatRelativeTime(lastMessageTime);
  const initials = getInitials(name);
  const roleCompanyLabel = [currentRole, company].filter(Boolean).join(" · ");

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex gap-3.5 items-center px-5 py-3.5 border-b border-theme-mld-border hover:bg-theme-mld-row-hover transition-colors text-left"
    >
      {/* Avatar — Radix Avatar rather than a bare <img> so a photoUrl that
          404s (deleted S3 object, or a key an endpoint forgot to resolve)
          falls back to the gradient + initials instead of an empty circle. */}
      <div className="relative flex-shrink-0">
        <Avatar className="size-10 border-0">
          {photoUrl ? (
            <AvatarImage
              src={photoUrl}
              alt={name}
              className="object-cover"
              style={{ filter: "none" }}
            />
          ) : null}
          <AvatarFallback
            className="text-[12px] font-bold text-white"
            style={{ background: avatarGradient }}
          >
            {initials}
          </AvatarFallback>
        </Avatar>
        {isOnline && <OnlineDot />}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-0.5">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-[14px] font-semibold text-theme-mld-heading truncate leading-5">
              {name}
            </span>
            {/* enrollee/peer render no pill: the section heading above already
                says what they are, and a badge on every row of a single-kind
                list is noise. */}
            {roleBadge && roleBadge !== "enrollee" && roleBadge !== "peer" && (
              <RoleBadge role={roleBadge} />
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
            {relTime && <span className="text-[11px] text-theme-text-muted">{relTime}</span>}
            {!!unreadCount && <UnreadBadge count={unreadCount} />}
          </div>
        </div>
        {lastMessage ? (
          <p className="text-[11px] text-theme-mld-preview-text truncate leading-[16.5px]">
            {lastMessage}
          </p>
        ) : roleCompanyLabel ? (
          <p className="text-[11px] text-theme-text-muted truncate leading-[16.5px]">
            {roleCompanyLabel}
          </p>
        ) : (
          <p className="text-[11px] text-theme-text-muted truncate leading-[16.5px] italic opacity-60">
            No messages yet
          </p>
        )}
      </div>
    </button>
  );
};

// ─── Inbox: one request for the whole list ────────────────────────────────────
//
// This replaced five fan-out hooks (program members, PAB group previews, program
// group previews, PAB DM previews, program DM previews). Each fired one request
// per row and downloaded a conversation's ENTIRE history just to read the last
// message off the end, so cost grew as O(programs x members): ~89 requests for a
// user with 14 programs, enough to trip the 50/minute per-IP limit on its own.
//
// GET /messages/inbox returns the same information in one response, with a fixed
// number of SQL queries regardless of how many programs or members the user has.
// If you need another field per row, add it to that endpoint — do NOT reintroduce
// a per-row fetch here.

const useInbox = (enabled: boolean) =>
  useQuery({
    queryKey: MESSAGES_INBOX_KEY,
    queryFn: () => api.messages.inbox(),
    enabled,
    staleTime: 30 * 1000,
  });

const MESSAGES_INBOX_KEY = ["messages", "inbox"] as const;

/** The preview shape the rows render from. `conversationId` is what drives the
 *  bulk mark-as-read; it replaced an `unreadIds` array that existed only to
 *  PATCH each unread message individually. */
interface RowPreview {
  conversationId: number | null;
  lastMessage: string | null;
  lastMessageTime: string | null;
  unreadCount: number;
}

const EMPTY_PREVIEW: RowPreview = {
  conversationId: null,
  lastMessage: null,
  lastMessageTime: null,
  unreadCount: 0,
};

/** Formats a conversation preview the way the old client-side derivePreview did:
 *  "You: ..." for your own last message, first name otherwise. */
const toRowPreview = (
  conversation: InboxConversationPreview | null,
  currentUserId?: string,
): RowPreview => {
  if (!conversation) return EMPTY_PREVIEW;
  const { lastMessageBody, lastMessageSenderId, lastMessageSenderName } = conversation;
  const senderLabel =
    currentUserId && lastMessageSenderId === currentUserId
      ? "You"
      : (lastMessageSenderName ?? "").split(" ")[0];
  return {
    conversationId: conversation.conversationId,
    // Flattened: the row is one truncated line, where a message's markdown
    // markers would read as noise rather than as formatting.
    lastMessage: lastMessageBody
      ? `${senderLabel}: ${stripMessageMarkdown(lastMessageBody)}`
      : null,
    lastMessageTime: conversation.lastMessageAt,
    unreadCount: conversation.unreadCount,
  };
};

// ─── Notes on what the inbox endpoint made unnecessary ───────────────────────
//
// The removed hooks each guarded against a real failure mode; the single request
// keeps those properties by construction rather than by client-side plumbing:
//
//  - skipForbiddenRedirect on every fetch. A 403 on ONE board used to trip the
//    axios interceptor's global `window.location.href = "/unauthorized"` and
//    eject an advisor from the app the moment they opened this drawer. The inbox
//    endpoint only ever returns conversations the caller participates in, so
//    there is no per-row authorisation to fail.
//  - per-row Promise.allSettled, so one bad row could not blank every other
//    row's preview. With one request there are no partial rows to settle.
//  - derivePreview()'s client-side sort/count over full histories. The server
//    now computes last-message and unread-count directly (window function +
//    grouped count), so no history is transferred to derive them.

// ─── Main component ───────────────────────────────────────────────────────────

interface MessagesListDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const MessagesListDrawer = ({ isOpen, onClose }: MessagesListDrawerProps) => {
  const { user } = useAuth();
  const isAdvisor = useActiveWorkspace() === "advisor";
  const { session } = usePabSession();
  const { data: advisorPabs } = useAdvisorPABs({ enabled: isAdvisor && isOpen });
  const { expertPrograms, advisorPrograms, peerPrograms, facilitatedPeerPrograms } =
    useExpertPrograms();
  const {
    openMessagesDrawer,
    closeMessagesListDrawer,
    openDirectProgramMessageDrawer,
    openDirectPabMessageDrawer,
  } = useDrawer();

  const qc = useQueryClient();
  const invalidateUnreadCount = () =>
    qc.invalidateQueries({ queryKey: ["messages", "unread-count"] });
  // Peer groups appear as group chats and DM sections exactly like Expert
  // Programs -- see the note in MessagesDrawer. The inbox endpoint already
  // returns them (it never filtered on program_type); this list was the only
  // thing leaving them out.
  const userPrograms = useMemo(
    () =>
      isAdvisor
        ? [...advisorPrograms, ...facilitatedPeerPrograms]
        : [...expertPrograms, ...peerPrograms],
    [isAdvisor, advisorPrograms, facilitatedPeerPrograms, expertPrograms, peerPrograms],
  );

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [tab, setTab] = useState<"inbox" | "unread">("inbox");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // PAB advisors (achiever view — show advisors they can DM)
  const { data: pabAdvisors } = useQuery({
    queryKey: ["messages-list-pab-advisors", session?.id],
    queryFn: () => api.pabSessions.getSelectedAdvisors(session!.id),
    enabled: !isAdvisor && isOpen && !!session?.id,
    staleTime: 5 * 60 * 1000,
  });

  // ── One request for every preview on the list ─────────────────────────────
  const { data: inbox } = useInbox(isOpen);

  /**
   * The inbox payload, reshaped into the lookups the rows below read.
   *
   * Group previews are keyed by program id, and a PAB session id IS a program
   * id, so PAB and Expert rows share one map. DM previews are keyed
   * "programId:userId", which is how every row already looks its DM up.
   */
  const { groupPreviews, dmPreviews, membersMap } = useMemo(() => {
    const groups: Record<number, RowPreview> = {};
    const dms: Record<string, RowPreview> = {};
    const members: Record<string, ProgramMembersGrouped> = {};

    (inbox?.programs ?? []).forEach((prog) => {
      groups[prog.programId] = toRowPreview(prog.groupConversation, user?.id);

      // Rebuilt in the shape the members endpoint returned, so the row-building
      // code below is unchanged. Only the fields these rows actually read are
      // populated — name, picture, role and company.
      const grouped: ProgramMembersGrouped = {
        lead_advisors: [],
        co_advisors: [],
        achievers: [],
      } as unknown as ProgramMembersGrouped;

      prog.participants.forEach((p) => {
        dms[`${prog.programId}:${p.userId}`] = toRowPreview(p.directConversation, user?.id);

        const profile = { current_role: p.currentRole, company: p.company };
        const member = {
          user_id: p.userId,
          full_name: p.fullName,
          profile_picture_url: p.profilePictureUrl,
          advisor_profile: profile,
          achiever_profile: profile,
        } as unknown as ProgramMembersGrouped["achievers"][number];

        // PEER (and an Organisation program's MEMBER) belong with ACHIEVER,
        // not in the catch-all: all are the people a program is FOR. Left to
        // fall through, they showed up in the "co-experts" DM section.
        if (
          p.membershipRole === "ACHIEVER" ||
          p.membershipRole === "PEER" ||
          p.membershipRole === "MEMBER"
        )
          grouped.achievers.push(member);
        else if (p.membershipRole === "LEAD") grouped.lead_advisors.push(member);
        else grouped.co_advisors.push(member);
      });

      if (prog.externalId) members[prog.externalId] = grouped;
    });

    return { groupPreviews: groups, dmPreviews: dms, membersMap: members };
  }, [inbox, user?.id]);

  // Both PAB and program group rows read from the same map now (see above).
  const pabGroupPreviews = groupPreviews;
  const programGroupPreviews = groupPreviews;
  // Likewise all three DM lookups: the key already encodes which program/PAB.
  const pabDmPreviews = dmPreviews;
  const advisorPabDmPreviews = dmPreviews;
  const programDmPreviews = dmPreviews;

  /** Clears a conversation's unread badge in one request, then refreshes the
   *  list. Replaced a Promise.all over unreadIds that PATCHed each message. */
  const markConversationRead = (preview?: RowPreview) => {
    if (!preview?.conversationId || preview.unreadCount === 0) return;
    void api.messages
      .markConversationRead(preview.conversationId)
      .then(() => {
        qc.invalidateQueries({ queryKey: MESSAGES_INBOX_KEY });
        invalidateUnreadCount();
      })
      .catch(() => {
        // Read receipts are best-effort; a failure must not block navigation.
      });
  };

  const acceptedAdvisorIds = (pabAdvisors ?? [])
    .filter(
      (a) =>
        a.approvalStatus?.toLowerCase() === "accepted" ||
        a.approvalStatus?.toLowerCase() === "approved",
    )
    .map((a) => a.userId);

  // The DM-preview key lists that used to live here are gone: they existed only
  // to drive one request per (board x participant) pair. dmPreviews above is
  // already keyed "programId:userId" for every conversation the user is in.

  // ── Derived: co-experts across all programs ──────────────────────────────
  const coExpertRows: {
    name: string;
    programId: number;
    programName: string;
    userId: string;
    photoUrl: string | null;
    avatarGradient: string;
    idx: number;
    currentRole: string | null;
    company: string | null;
    /** Drives the row's badge wording -- Facilitator/Peer vs Co-Expert/Enrollee. */
    isPeer: boolean;
  }[] = [];

  const enrolleeRows: {
    name: string;
    programId: number;
    programName: string;
    userId: string;
    photoUrl: string | null;
    avatarGradient: string;
    idx: number;
    currentRole: string | null;
    company: string | null;
    /** Drives the row's badge wording -- Facilitator/Peer vs Co-Expert/Enrollee. */
    isPeer: boolean;
  }[] = [];

  userPrograms.forEach((prog) => {
    const members = membersMap?.[prog.externalId];
    if (!members) return;
    // Organisation programs are also run by facilitators, so their staff get
    // the facilitator badge rather than "co-expert".
    const isPeerProgram =
      prog.programType === "PEER_ADVISORY" || prog.programType === "ORGANISATION";

    // Include both lead advisors and co-advisors so all advisors are DM-able
    // (on a peer group: the lead facilitator and any co-facilitators).
    [...(members.lead_advisors ?? []), ...members.co_advisors].forEach((m) => {
      if (!m.user_id || !m.full_name || m.user_id === user?.id) return;
      coExpertRows.push({
        name: m.full_name,
        programId: prog.id,
        programName: prog.name,
        userId: m.user_id,
        photoUrl: m.profile_picture_url,
        avatarGradient: AVATAR_GRADIENTS[coExpertRows.length % AVATAR_GRADIENTS.length],
        idx: coExpertRows.length,
        currentRole: m.advisor_profile?.current_role ?? null,
        company: m.advisor_profile?.company ?? null,
        isPeer: isPeerProgram,
      });
    });

    members.achievers.forEach((m) => {
      if (!m.user_id || !m.full_name || m.user_id === user?.id) return;
      enrolleeRows.push({
        name: m.full_name,
        programId: prog.id,
        programName: prog.name,
        userId: m.user_id,
        photoUrl: m.profile_picture_url,
        avatarGradient: AVATAR_GRADIENTS[enrolleeRows.length % AVATAR_GRADIENTS.length],
        idx: enrolleeRows.length,
        currentRole: m.achiever_profile?.current_role ?? null,
        company: m.achiever_profile?.company ?? null,
        isPeer: isPeerProgram,
      });
    });
  });

  /** Names a section by what is actually in it, so a user with both an Expert
   *  Program and a peer group gets one honest heading rather than a wrong one. */
  const sectionLabelFor = (rows: { isPeer: boolean }[], expertWord: string, peerWord: string) => {
    if (rows.every((r) => r.isPeer)) return peerWord;
    if (rows.every((r) => !r.isPeer)) return expertWord;
    return `${expertWord} & ${peerWord}`;
  };

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleGroupPabClick = () => {
    closeMessagesListDrawer();
    const pabTitle = session?.name ?? "PAB";
    // A group-chat row opens the GROUP thread, the one its unread count is for.
    openMessagesDrawer(undefined, session?.id ?? undefined, undefined, pabTitle, undefined, {
      groupThread: true,
    });
  };

  const handleGroupProgramClick = (programExternalId: string, chatTitle: string) => {
    closeMessagesListDrawer();
    openMessagesDrawer(undefined, undefined, programExternalId, chatTitle);
  };

  const handlePabAdvisorDmClick = (advisor: SelectedAdvisor) => {
    closeMessagesListDrawer();
    const pabSubtitle = session?.name ?? "PAB";
    openDirectPabMessageDrawer(session!.id, pabSubtitle, [
      { userId: advisor.userId, name: advisor.name },
    ]);
  };

  const handleDmClick = (programId: number, programName: string, userId: string, name: string) => {
    closeMessagesListDrawer();
    const recipients: DirectMessageRecipient[] = [{ userId, name }];
    openDirectProgramMessageDrawer(programId, programName, recipients);
  };

  // ── Group chats: PAB + programs ───────────────────────────────────────────

  const groupChats: {
    id: string;
    name: string;
    colorIdx: number;
    onClick: () => void;
    lastMessage?: string | null;
    lastMessageTime?: string | null;
    unreadCount?: number;
    memberCount?: number;
  }[] = [];

  if (!isAdvisor && session?.id) {
    const pabLabel = session.name ?? "PAB";
    const prev = pabGroupPreviews?.[session.id];
    const sid = session.id;
    groupChats.push({
      id: `pab-${sid}`,
      name: pabLabel,
      colorIdx: 0,
      onClick: () => {
        markConversationRead(prev);
        handleGroupPabClick();
      },
      lastMessage: prev?.lastMessage,
      lastMessageTime: prev?.lastMessageTime,
      unreadCount: prev?.unreadCount,
      memberCount: acceptedAdvisorIds.length + 1,
    });
  }

  if (isAdvisor) {
    advisorPabs?.forEach((pab, i) => {
      const label = pab.name ?? "PAB";
      const prev = pabGroupPreviews?.[pab.pabSessionId];
      groupChats.push({
        id: `advisor-pab-${pab.pabSessionId}`,
        name: label,
        colorIdx: i,
        onClick: () => {
          markConversationRead(prev);
          closeMessagesListDrawer();
          // The group thread — this row's preview and unread count are the
          // group conversation's, not the 1:1 with the achiever.
          openMessagesDrawer(undefined, pab.pabSessionId, undefined, label, undefined, {
            groupThread: true,
          });
        },
        lastMessage: prev?.lastMessage,
        lastMessageTime: prev?.lastMessageTime,
        unreadCount: prev?.unreadCount,
        memberCount: pab.advisors.length + 1,
      });
    });
  }

  userPrograms.forEach((prog, i) => {
    const prev = programGroupPreviews?.[prog.id];
    const members = membersMap?.[prog.externalId];
    const coCount = members?.co_advisors.filter((m) => !!m.user_id).length ?? 0;
    const achCount = members?.achievers.filter((m) => !!m.user_id).length ?? 0;
    groupChats.push({
      id: `program-${prog.externalId}`,
      name: prog.name,
      colorIdx: isAdvisor ? (advisorPabs?.length ?? 0) + i : 1 + i,
      onClick: () => {
        markConversationRead(prev);
        handleGroupProgramClick(prog.externalId, prog.name);
      },
      lastMessage: prev?.lastMessage,
      lastMessageTime: prev?.lastMessageTime,
      unreadCount: prev?.unreadCount,
      memberCount: coCount + achCount > 0 ? coCount + achCount : undefined,
    });
  });

  // ── PAB DM section (achiever: shows accepted advisors; advisor: handled per-PAB below) ──

  const pabSectionLabel = !isAdvisor ? (session?.name ?? "PAB") : null;

  const acceptedPabAdvisors = (pabAdvisors ?? []).filter(
    (a) =>
      a.approvalStatus?.toLowerCase() === "accepted" ||
      a.approvalStatus?.toLowerCase() === "approved",
  );

  // ── DM rows, built once: the Inbox sections and the Unread tab render the
  // same descriptors, so a row behaves identically in both. ─────────────────
  interface DmItem {
    key: string;
    name: string;
    roleBadge?: "pab_advisor" | "co_expert" | "facilitator" | "enrollee" | "peer";
    avatarGradient: string;
    photoUrl?: string | null;
    currentRole?: string | null;
    company?: string | null;
    preview?: RowPreview;
    onClick: () => void;
  }

  const achieverPabDmItems: DmItem[] = !isAdvisor
    ? acceptedPabAdvisors.map((advisor, i) => {
        const prev = pabDmPreviews?.[`${session!.id}:${advisor.userId}`];
        return {
          key: `pab-dm-${advisor.userId}`,
          name: advisor.name,
          roleBadge: "pab_advisor" as const,
          avatarGradient: AVATAR_GRADIENTS[i % AVATAR_GRADIENTS.length],
          photoUrl: advisor.profileUrl,
          currentRole: advisor.currentTitle,
          company: advisor.currentCompany,
          preview: prev,
          onClick: () => {
            markConversationRead(prev);
            handlePabAdvisorDmClick(advisor);
          },
        };
      })
    : [];

  const advisorPabSections: { key: string; label: string; items: DmItem[] }[] = isAdvisor
    ? (advisorPabs ?? []).map((pab, i) => {
        const otherAdvisors = pab.advisors.filter((a) => !a.isCurrentAdvisor);
        const sectionLabel = pab.name ?? "PAB";
        const achieverPrev = pab.achiever.userId
          ? advisorPabDmPreviews?.[`${pab.pabSessionId}:${pab.achiever.userId}`]
          : undefined;
        const items: DmItem[] = [
          {
            key: `advisor-pab-${pab.pabSessionId}-achiever`,
            name: pab.achiever.fullName,
            avatarGradient: AVATAR_GRADIENTS[i % AVATAR_GRADIENTS.length],
            photoUrl: pab.achiever.profilePictureUrl,
            currentRole: pab.achiever.currentRole,
            company: pab.achiever.currentCompany,
            preview: achieverPrev,
            onClick: () => {
              markConversationRead(achieverPrev);
              closeMessagesListDrawer();
              openDirectPabMessageDrawer(pab.pabSessionId, sectionLabel, [
                { userId: pab.achiever.userId!, name: pab.achiever.fullName },
              ]);
            },
          },
          ...otherAdvisors.map((advisor, j) => {
            const coPrev = advisor.userId
              ? advisorPabDmPreviews?.[`${pab.pabSessionId}:${advisor.userId}`]
              : undefined;
            return {
              key: `advisor-pab-${pab.pabSessionId}-${advisor.id}`,
              name: advisor.name,
              roleBadge: "pab_advisor" as const,
              avatarGradient: AVATAR_GRADIENTS[(i + j + 1) % AVATAR_GRADIENTS.length],
              currentRole: advisor.title,
              company: advisor.company,
              preview: coPrev,
              onClick: () => {
                markConversationRead(coPrev);
                closeMessagesListDrawer();
                openDirectPabMessageDrawer(pab.pabSessionId, sectionLabel, [
                  { userId: advisor.userId!, name: advisor.name },
                ]);
              },
            };
          }),
        ];
        return { key: `advisor-pab-dm-${pab.pabSessionId}`, label: sectionLabel, items };
      })
    : [];

  const programDmItem = (
    row: (typeof coExpertRows)[number],
    roleBadge?: DmItem["roleBadge"],
  ): DmItem => {
    const prev = programDmPreviews?.[`${row.programId}:${row.userId}`];
    return {
      key: `${row.programId}-${row.userId}`,
      name: row.name,
      roleBadge,
      avatarGradient: row.avatarGradient,
      photoUrl: row.photoUrl,
      currentRole: row.currentRole,
      company: row.company,
      preview: prev,
      onClick: () => {
        markConversationRead(prev);
        handleDmClick(row.programId, row.programName, row.userId, row.name);
      },
    };
  };
  const coExpertItems = coExpertRows.map((row) =>
    programDmItem(row, row.isPeer ? "facilitator" : "co_expert"),
  );
  const enrolleeItems = enrolleeRows.map((row) => programDmItem(row));

  const renderDm = (item: DmItem) => (
    <DmRow
      key={item.key}
      name={item.name}
      roleBadge={item.roleBadge}
      avatarGradient={item.avatarGradient}
      photoUrl={item.photoUrl}
      currentRole={item.currentRole}
      company={item.company}
      onClick={item.onClick}
      lastMessage={item.preview?.lastMessage}
      lastMessageTime={item.preview?.lastMessageTime}
      unreadCount={item.preview?.unreadCount}
    />
  );

  // ── Unread: every conversation with unread messages, newest first ─────────
  const timeOf = (iso?: string | null) => (iso ? new Date(iso).getTime() || 0 : 0);
  const unreadItems = [
    ...groupChats
      .filter((c) => !!c.unreadCount)
      .map((c) => ({ key: c.id, time: timeOf(c.lastMessageTime), group: c, dm: undefined })),
    ...[
      ...achieverPabDmItems,
      ...advisorPabSections.flatMap((sec) => sec.items),
      ...coExpertItems,
      ...enrolleeItems,
    ]
      .filter((d) => !!d.preview?.unreadCount)
      .map((d) => ({
        key: d.key,
        time: timeOf(d.preview?.lastMessageTime),
        group: undefined,
        dm: d,
      })),
  ].sort((a, b) => b.time - a.time);

  // ── Close dropdown on outside click ──────────────────────────────────────
  useEffect(() => {
    if (!dropdownOpen) return;
    const handle = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [dropdownOpen]);

  // ── Filter options — "All Messages" toggles the dropdown closed; every other
  // entry jumps straight into that PAB/program's group chat instead of
  // filtering this same list in place. ─────────────────────────────────────
  const filterOptions: Array<{
    key: string;
    label: string;
    sublabel: string;
    type: "all" | "pab" | "program";
    onOpenChat?: () => void;
  }> = [{ key: "all", label: "All Messages", sublabel: "Everyone", type: "all" }];
  if (!isAdvisor && session?.id) {
    const sid = session.id;
    filterOptions.push({
      key: `pab-${sid}`,
      label: session.name ?? "PAB",
      sublabel: `${acceptedAdvisorIds.length} expert${acceptedAdvisorIds.length !== 1 ? "s" : ""}`,
      type: "pab",
      onOpenChat: groupChats.find((c) => c.id === `pab-${sid}`)?.onClick,
    });
  }
  if (isAdvisor) {
    (advisorPabs ?? []).forEach((pab) => {
      const lbl = pab.name ?? "PAB";
      const cnt = 1 + pab.advisors.filter((a) => !a.isCurrentAdvisor).length;
      filterOptions.push({
        key: `pab-${pab.pabSessionId}`,
        label: lbl,
        sublabel: `${cnt} expert${cnt !== 1 ? "s" : ""}`,
        type: "pab",
        onOpenChat: groupChats.find((c) => c.id === `advisor-pab-${pab.pabSessionId}`)?.onClick,
      });
    });
  }
  userPrograms.forEach((prog) => {
    const members = membersMap?.[prog.externalId];
    const eCount = members?.achievers.filter((m) => !!m.user_id).length ?? 0;
    const cCount = members?.co_advisors.filter((m) => !!m.user_id).length ?? 0;
    filterOptions.push({
      key: `prog-${prog.id}`,
      label: prog.name,
      sublabel: `${eCount} enrollee${eCount !== 1 ? "s" : ""} · ${cCount} co-expert${cCount !== 1 ? "s" : ""}`,
      type: "program",
      onOpenChat: groupChats.find((c) => c.id === `program-${prog.externalId}`)?.onClick,
    });
  });

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30" onClick={onClose} aria-hidden />

      {/* Panel */}
      <div
        className="absolute right-0 top-0 h-full w-full max-w-[460px] flex flex-col overflow-hidden border-l border-theme-mld-border shadow-2xl"
        style={{ background: "var(--theme-mld-panel-bg)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-theme-mld-border flex-shrink-0">
          <Heading
            as="h2"
            className="text-[24px] font-semibold text-theme-mld-heading tracking-[-0.17px]"
          >
            Messages
          </Heading>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-[10px] hover:bg-theme-mld-close-hover-bg transition-colors text-theme-mld-control-icon hover:text-theme-mld-heading"
          >
            <X size={18} />
          </button>
        </div>

        {/* Inbox / Unread tabs */}
        <div
          role="tablist"
          aria-label="Messages"
          className="flex gap-6 px-5 border-b border-theme-mld-border flex-shrink-0"
        >
          {(
            [
              { id: "inbox", label: "Inbox", count: undefined },
              { id: "unread", label: "Unread", count: unreadItems.length },
            ] as const
          ).map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t.id)}
                className={`-mb-px flex items-center gap-1.5 border-b-2 py-3 text-[14px] font-semibold transition-colors ${
                  active
                    ? "border-primary-purple text-theme-mld-heading"
                    : "border-transparent text-theme-text-muted hover:text-theme-mld-heading"
                }`}
              >
                {t.label}
                {!!t.count && <UnreadBadge count={t.count} />}
              </button>
            );
          })}
        </div>

        {/* Filter pill — Inbox only */}
        {tab === "inbox" && (
          <div
            className="px-4 py-3 border-b border-theme-mld-border flex-shrink-0 relative"
            ref={dropdownRef}
          >
            <button
              type="button"
              onClick={() => setDropdownOpen((o) => !o)}
              className="flex items-center gap-2.5 bg-theme-mld-filter-bg rounded-[14px] px-3.5 py-2.5 w-full hover:bg-theme-mld-filter-hover transition-colors"
            >
              <Users size={14} className="text-theme-mld-control-icon flex-shrink-0" />
              <span className="text-[14px] font-medium text-theme-mld-heading leading-5 truncate">
                All Messages
              </span>
              <ChevronDown
                size={14}
                className={`text-theme-mld-control-icon ml-auto flex-shrink-0 transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}
              />
            </button>

            {/* Dropdown panel — "All Messages" just closes the dropdown (this list
              already shows everything); every other entry jumps straight into
              that PAB/program's group chat. */}
            {dropdownOpen && (
              <div className="absolute left-4 right-4 top-[calc(100%-4px)] z-10 rounded-[16px] border border-theme-mld-dropdown-border bg-theme-mld-dropdown-bg overflow-y-auto max-h-[60vh] shadow-[0px_20px_25px_-5px_rgba(0,0,0,0.3),0px_8px_10px_-6px_rgba(0,0,0,0.2)]">
                {filterOptions.map((opt, idx) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => {
                      setDropdownOpen(false);
                      if (opt.onOpenChat) opt.onOpenChat();
                    }}
                    className={`w-full flex items-center justify-between px-4 py-3 hover:bg-theme-mld-dropdown-item-hover transition-colors text-left${idx < filterOptions.length - 1 ? " border-b border-theme-mld-dropdown-item-border" : ""}`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div
                        className="size-7 rounded-[10px] flex items-center justify-center flex-shrink-0"
                        style={{
                          background:
                            opt.type === "program" ? "rgba(173,70,255,0.1)" : "rgba(97,95,255,0.1)",
                        }}
                      >
                        {opt.type === "program" ? (
                          <BookOpen size={13} style={{ color: "#a855f7" }} />
                        ) : (
                          <Users size={13} style={{ color: "#615fff" }} />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-[14px] font-medium text-theme-mld-heading leading-5 truncate">
                          {opt.label}
                        </p>
                        <p className="text-[11px] leading-[16.5px] text-theme-mld-preview-text">
                          {opt.sublabel}
                        </p>
                      </div>
                    </div>
                    {opt.key === "all" && (
                      <Check size={14} className="text-theme-mld-heading flex-shrink-0 ml-2" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Scrollable list — always the full "All Messages" view now that
            picking a specific PAB/program from the dropdown navigates
            straight into its chat instead of filtering this list. */}
        <div className="flex-1 overflow-y-auto">
          {tab === "unread" ? (
            unreadItems.length > 0 ? (
              unreadItems.map((item) =>
                item.group ? (
                  <GroupChatRow
                    key={item.key}
                    name={item.group.name}
                    colorIndex={item.group.colorIdx}
                    onClick={item.group.onClick}
                    lastMessage={item.group.lastMessage}
                    lastMessageTime={item.group.lastMessageTime}
                    unreadCount={item.group.unreadCount}
                    memberCount={item.group.memberCount}
                  />
                ) : (
                  item.dm && renderDm(item.dm)
                ),
              )
            ) : (
              <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                <div className="size-14 rounded-2xl bg-theme-mld-empty-icon-bg flex items-center justify-center mb-4">
                  <Check size={22} className="text-theme-mld-empty-icon" />
                </div>
                <Text as="p" className="text-[14px] font-semibold text-theme-mld-empty-text mb-1">
                  You&apos;re all caught up
                </Text>
                <Text as="p" className="text-[13px] text-theme-text-muted">
                  New messages you haven&apos;t read will show up here, newest first.
                </Text>
              </div>
            )
          ) : (
            <>
              {/* ── Group Chats ──────────────────────────────────────── */}
              {groupChats.length > 0 && (
                <div>
                  <SectionHeader label="Group Chats" count={groupChats.length} />
                  {groupChats.map((chat) => (
                    <GroupChatRow
                      key={chat.id}
                      name={chat.name}
                      colorIndex={chat.colorIdx}
                      onClick={chat.onClick}
                      lastMessage={chat.lastMessage}
                      lastMessageTime={chat.lastMessageTime}
                      unreadCount={chat.unreadCount}
                      memberCount={chat.memberCount}
                    />
                  ))}
                </div>
              )}

              {/* ── PAB DMs: achiever view ────────────────────────────── */}
              {achieverPabDmItems.length > 0 && pabSectionLabel && (
                <div>
                  <SectionHeader label={pabSectionLabel} count={achieverPabDmItems.length} />
                  {achieverPabDmItems.map(renderDm)}
                </div>
              )}

              {/* ── PAB DMs: advisor view ─────────────────────────────── */}
              {advisorPabSections.map((sec) => (
                <div key={sec.key}>
                  <SectionHeader label={sec.label} count={sec.items.length} />
                  {sec.items.map(renderDm)}
                </div>
              ))}

              {/* ── Co-Experts ───────────────────────────────────────── */}
              {coExpertItems.length > 0 && (
                <div>
                  <SectionHeader
                    label={sectionLabelFor(coExpertRows, "Co-Experts", "Facilitators")}
                    count={coExpertItems.length}
                  />
                  {coExpertItems.map(renderDm)}
                </div>
              )}

              {/* ── Enrollees ─────────────────────────────────────────── */}
              {enrolleeItems.length > 0 && (
                <div>
                  <SectionHeader
                    label={sectionLabelFor(enrolleeRows, "Enrollees", "Peers")}
                    count={enrolleeItems.length}
                  />
                  {enrolleeItems.map(renderDm)}
                </div>
              )}

              {/* Empty state */}
              {groupChats.length === 0 &&
                acceptedPabAdvisors.length === 0 &&
                (advisorPabs ?? []).length === 0 &&
                coExpertRows.length === 0 &&
                enrolleeRows.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                    <div className="size-14 rounded-2xl bg-theme-mld-empty-icon-bg flex items-center justify-center mb-4">
                      <Hash size={22} className="text-theme-mld-empty-icon" />
                    </div>
                    <Text
                      as="p"
                      className="text-[14px] font-semibold text-theme-mld-empty-text mb-1"
                    >
                      No conversations yet
                    </Text>
                    <Text as="p" className="text-[13px] text-theme-text-muted">
                      Your conversations will appear here once you join a Personal Advisory Board or
                      program.
                    </Text>
                  </div>
                )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};
